import { useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { FileText, MagnifyingGlass, CalendarBlank, Warning } from "@phosphor-icons/react";

const statusColors = { active: "#00B341", expired: "#6B7280", pending: "#FFD100" };

const parseYear = (d) => { if (!d || d.length < 4) return null; try { return parseInt(d.substring(0, 4)); } catch { return null; } };
const parseMonth = (d) => { if (!d || d.length < 7) return null; try { return parseInt(d.substring(5, 7)); } catch { return null; } };
const fmt = (n) => (!n || n === 0) ? "$0" : `$${Number(n).toLocaleString()}`;

export default function MultiYearContracts() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    api.get("/contracts/multi-year").then((r) => setData(r.data)).catch(() => toast.error("Failed to load")).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-8 flex items-center justify-center h-full"><p className="text-sm text-[#6B7280]">Loading contracts...</p></div>;
  if (!data) return null;

  const { clients, year_range } = data;
  const filtered = search ? clients.filter((c) => JSON.stringify(c).toLowerCase().includes(search.toLowerCase())) : clients;
  const minYear = year_range[0] || 2024;
  const maxYear = year_range[year_range.length - 1] || 2027;
  const totalMonths = (maxYear - minYear + 1) * 12;

  const getBarStyle = (contract) => {
    const startY = parseYear(contract.start_date);
    const startM = parseMonth(contract.start_date) || 1;
    const endY = parseYear(contract.end_date);
    const endM = parseMonth(contract.end_date) || 12;
    if (!startY || !endY) return null;
    const startOffset = (startY - minYear) * 12 + (startM - 1);
    const endOffset = (endY - minYear) * 12 + endM;
    const left = Math.max(0, (startOffset / totalMonths) * 100);
    const width = Math.max(2, ((endOffset - startOffset) / totalMonths) * 100);
    return { left: `${left}%`, width: `${Math.min(width, 100 - left)}%` };
  };

  const today = new Date();
  const todayOffset = ((today.getFullYear() - minYear) * 12 + today.getMonth()) / totalMonths * 100;

  return (
    <div className="p-6" data-testid="multi-year-contracts-page">
      <div className="mb-6">
        <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#6B7280] mb-1">Management</p>
        <h1 className="text-2xl sm:text-3xl tracking-tight font-semibold text-[#1A1D20]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Multi-Year Contract Tracking</h1>
        <p className="text-sm text-[#6B7280] mt-1">Visual timeline of contract durations across funding years</p>
      </div>

      <div className="mb-4 relative max-w-sm">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" />
        <Input data-testid="search-multi-year" placeholder="Search clients..." value={search} onChange={(e) => setSearch(e.target.value)} className="rounded-none pl-9" />
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white border border-gray-200 p-12 text-center">
          <FileText size={32} className="mx-auto text-[#6B7280] mb-3" />
          <p className="text-sm text-[#6B7280]">No contracts found. Add contracts with start/end dates to see the timeline.</p>
        </div>
      ) : (
        <div className="bg-white border border-gray-200" data-testid="contract-timeline">
          {/* Year headers */}
          <div className="flex border-b border-gray-200">
            <div className="w-[220px] flex-shrink-0 p-3 border-r border-gray-200">
              <span className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#6B7280]">Client</span>
            </div>
            <div className="flex-1 relative flex">
              {year_range.map((year) => (
                <div key={year} className="flex-1 p-2 border-r border-gray-100 text-center">
                  <span className="text-xs font-semibold text-[#6B7280]">{year}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Client rows */}
          {filtered.map((client) => (
            <div key={client.client_name} className="flex border-b border-gray-100 hover:bg-gray-50 transition-colors" data-testid={`client-row-${client.client_id}`}>
              {/* Client info */}
              <div className="w-[220px] flex-shrink-0 p-3 border-r border-gray-200">
                <p className="text-sm font-medium text-[#1A1D20] truncate">{client.client_name}</p>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-[10px] text-[#6B7280]">{client.contracts.length} contract{client.contracts.length !== 1 ? "s" : ""}</span>
                  <span className="text-[10px] font-medium text-[#002FA7]">{fmt(client.total_value)}</span>
                </div>
              </div>
              {/* Timeline */}
              <div className="flex-1 relative py-2 px-1" style={{ minHeight: `${Math.max(40, client.contracts.length * 28 + 8)}px` }}>
                {/* Year grid lines */}
                {year_range.map((year, i) => (
                  <div key={year} className="absolute top-0 bottom-0 border-r border-gray-100" style={{ left: `${((i + 1) / year_range.length) * 100}%` }} />
                ))}
                {/* Today marker */}
                {todayOffset >= 0 && todayOffset <= 100 && (
                  <div className="absolute top-0 bottom-0 w-px bg-[#FF2A00]/40 z-10" style={{ left: `${todayOffset}%` }} />
                )}
                {/* Contract bars */}
                {client.contracts.map((contract, ci) => {
                  const style = getBarStyle(contract);
                  if (!style) return null;
                  const color = statusColors[contract.status] || "#6B7280";
                  return (
                    <div
                      key={contract.id}
                      className="absolute h-5 flex items-center px-1.5 cursor-default group"
                      style={{ ...style, top: `${ci * 28 + 4}px`, backgroundColor: `${color}20`, borderLeft: `3px solid ${color}` }}
                      title={`${contract.title} | ${contract.service_provider} | ${contract.start_date} - ${contract.end_date} | ${fmt(contract.contract_value)}`}
                      data-testid={`contract-bar-${contract.id}`}
                    >
                      <span className="text-[9px] font-medium truncate text-[#1A1D20]">{contract.title || contract.service_provider}</span>
                      {/* Tooltip on hover */}
                      <div className="hidden group-hover:block absolute bottom-full left-0 mb-1 bg-[#1A1D20] text-white text-[10px] px-3 py-2 z-20 whitespace-nowrap">
                        <p className="font-semibold">{contract.title}</p>
                        <p>{contract.service_provider} {contract.spin && `(SPIN: ${contract.spin})`}</p>
                        <p>{contract.start_date} — {contract.end_date}</p>
                        <p>{fmt(contract.contract_value)} | <span className="capitalize">{contract.status}</span></p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          {/* Legend */}
          <div className="flex items-center gap-6 px-4 py-3 bg-[#F8F9FA] border-t border-gray-200">
            <span className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#6B7280]">Legend:</span>
            {Object.entries(statusColors).map(([status, color]) => (
              <div key={status} className="flex items-center gap-1.5">
                <div className="w-3 h-3" style={{ backgroundColor: `${color}20`, borderLeft: `3px solid ${color}` }} />
                <span className="text-xs capitalize text-[#6B7280]">{status}</span>
              </div>
            ))}
            <div className="flex items-center gap-1.5">
              <div className="w-px h-3 bg-[#FF2A00]/40" />
              <span className="text-xs text-[#6B7280]">Today</span>
            </div>
          </div>
        </div>
      )}

      {/* Renewal alerts */}
      {(() => {
        const today = new Date().toISOString().split("T")[0];
        const expiringSoon = [];
        filtered.forEach((client) => {
          client.contracts.forEach((c) => {
            if (c.status === "active" && c.end_date) {
              const daysLeft = Math.floor((new Date(c.end_date) - new Date()) / 86400000);
              if (daysLeft <= 90 && daysLeft > -30) {
                expiringSoon.push({ ...c, client_name: client.client_name, daysLeft });
              }
            }
          });
        });
        if (expiringSoon.length === 0) return null;
        return (
          <div className="mt-6 bg-white border border-[#FFD100]/30 p-5" data-testid="renewal-alerts">
            <div className="flex items-center gap-2 mb-3">
              <Warning size={16} className="text-[#946B00]" weight="bold" />
              <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#946B00]">Renewal Alerts ({expiringSoon.length})</p>
            </div>
            <div className="space-y-2">
              {expiringSoon.sort((a, b) => a.daysLeft - b.daysLeft).map((c) => (
                <div key={c.id} className="flex items-center justify-between py-2 px-3 border border-gray-100">
                  <div>
                    <p className="text-sm text-[#1A1D20]"><span className="font-medium">{c.client_name}</span> — {c.title}</p>
                    <p className="text-xs text-[#6B7280]">{c.service_provider} | Ends: {c.end_date}</p>
                  </div>
                  <Badge className={`rounded-none text-xs border ${c.daysLeft < 0 ? "bg-[#FF2A00]/10 text-[#FF2A00] border-[#FF2A00]/20" : c.daysLeft <= 30 ? "bg-[#FFD100]/10 text-[#946B00] border-[#FFD100]/20" : "bg-[#002FA7]/10 text-[#002FA7] border-[#002FA7]/20"}`}>
                    {c.daysLeft < 0 ? `${Math.abs(c.daysLeft)}d expired` : `${c.daysLeft}d left`}
                  </Badge>
                </div>
              ))}
            </div>
          </div>
        );
      })()}
    </div>
  );
}
